import { useState, useEffect } from "react";
import DataTable, { Column } from "../../components/DataTable";
import { Student } from "../../types/studentType";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  getStudents,
  setPagination,
  setSearchFilters,
} from "../../features/student/StudentSlice";

export default function Students({
  onSelect,
  selectedStudents,
  currentClassId,
}: {
  onSelect: React.Dispatch<React.SetStateAction<number[]>>;
  selectedStudents: number[];
  currentClassId: number;
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { classId } = useParams();

  // const [searchParams] = useSearchParams();
  // const academicCycleId = searchParams.get("academicCycleId");

  const { students, isLoading, pagination, searchFilters } = useSelector(
    (state: RootState) => state.student,
  );

  const handleCheckboxChange = (id: number, checked: boolean) => {
    onSelect((prev) => {
      const updated = checked ? [...prev, id] : prev.filter((s) => s !== id);
      return updated;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = students.map((s) => s.admissionNumber);
      onSelect(allIds);
    } else {
      onSelect([]);
    }
  };

  const { schoolBranch } = useSelector((state: RootState) => state.access);

  const branchCode = schoolBranch?.schoolBranchCode;

  useEffect(() => {
    dispatch(
      getStudents({
        page: pagination.currentPage,
        size: 100,
        studentName: searchFilters.studentName,
        parentContact: searchFilters.parentContact,
        studentAdmissionSequence: searchFilters.studentAdmissionSequence,
        rollNo: searchFilters.rollNo,
        branchCode,
        classId,
        // academicCycleId: academicCycleId ? Number(academicCycleId) : undefined,
      }),
    );
  }, [
    dispatch,
    pagination.currentPage,
    pagination.pageSize,
    searchFilters,
    branchCode,
    classId,
  ]);

  const handleViewProfile = (admissionNumber: number) => {
    navigate(`/all-students/student-profile/${admissionNumber}`);
  };

  const tableData: Student[] = students.map((student) => ({
    ...student,
    id: student.admissionNumber,
    isInCurrentClass: student.classId === currentClassId,
  }));

  const columns: Column<Student>[] = [
    {
      label: (
        <input
          type="checkbox"
          className="w-3 h-3"
          checked={
            selectedStudents.length === students.length && students.length > 0
          }
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      accessor: "select",
      render: (item) => (
        <input
          type="checkbox"
          className="w-3 h-3"
          checked={selectedStudents.includes(item.admissionNumber)}
          onChange={(e) =>
            handleCheckboxChange(item.admissionNumber, e.target.checked)
          }
        />
      ),
    },
    {
      label: "SL NO.",
      accessor: "studentAdmissionSequence",
      searchable: true,
      render: (item) => <p>{item?.studentAdmissionSequence || "---"}</p>,
    },
    {
      label: "Roll NO",
      accessor: "rollNo",
      searchable: true,
      render: (item) => <p>{item?.rollNo || "----"}</p>,
    },
    {
      label: "Profile",
      accessor: "profile",
      render: (item) => (
        <div>
          {!item?.photoUrl ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.photoUrl}
              alt=""
              className="flex items-center justify-center object-cover w-10 h-10 rounded-full"
            />
          )}
        </div>
      ),
    },
    {
      label: "Student Name",
      accessor: "studentName",
      searchable: true,
      render: (item) => (
        <div>
          <div className="font-bold text-sm text-neutral-400">
            {item.fullName}
          </div>
          <div className="text-xs text-neutral-300">
            DOB - {item.dateOfBirth}
          </div>
        </div>
      ),
    },
    {
      label: "Parent Contact",
      accessor: "parentContact",
      searchable: true,
      render: (item) => (
        <div>
          <div className="text-neutral-400 text-sm">
            {item.motherEmail ||
              item.fatherEmail ||
              item.motherFullName ||
              item.fatherFullName}
          </div>
          <div className="text-neutral-400 text-sm">
            {item.motherPhoneNumber || item.fatherPhoneNumber}
          </div>
        </div>
      ),
    },
    // {
    //   label: "Others",
    //   accessor: "others",
    //   render: (item) => (
    //     <div className="flex gap-3">
    //       <span
    //         className={`px-2 py-1 text-xs font-medium rounded-[4px] bg-success-50 text-success-600`}
    //       >
    //         Daycare
    //       </span>
    //       <span
    //         className={`px-2 py-1 text-xs font-medium rounded-[4px] bg-primary-50 text-primary-600`}
    //       >
    //         Yellow House
    //       </span>
    //     </div>
    //   ),
    // },
    {
      label: "Action",
      accessor: "action",
      searchable: false,
      render: (item) => (
        <button
          onClick={() => handleViewProfile(item.admissionNumber)}
          className="px-2 py-1 text-nowrap text-xs text-warning-700 bg-warning-50 rounded-[4px] font-medium"
        >
          View Profile
        </button>
      ),
    },
  ];

  return (
    <div className="bg-white px-6 py-5 rounded-lg">
      <DataTable
        columns={columns}
        data={tableData}
        searchFilters={searchFilters}
        onSearchChange={(accessor, value) => {
          dispatch(setSearchFilters({ [accessor]: value }));
          dispatch(
            setPagination({ currentPage: 0, pageSize: pagination.pageSize }),
          );
        }}
        isLoading={isLoading}
      />
    </div>
  );
}
